import { createTheme } from "@mui/material/styles";
import { rawColor, colors } from "../constants/colors";

export const stoneTheme = createTheme({
  name: "stone",
  palette: {
    mode: "light",
    primary: { main: rawColor.stone.main },
    secondary: { main: rawColor.stone.secondary },
    background: {
      default: rawColor.stone.background,
      paper: rawColor.stone.paper,
    },
    text: {
      primary: rawColor.stone.text.primary,
      secondary: rawColor.stone.text.secondary,
    },
    action: {
      hover: rawColor.stone.sidebar.hover,
    },
    error: { main: rawColor.status.error.light },
    warning: { main: rawColor.status.warning },
    success: { main: rawColor.status.active.green },
    divider: rawColor.stone.divider,
  },
  sidebar: {
    base: colors.sidebar.stone.base,
    hover: colors.sidebar.stone.hover,
    selected: colors.sidebar.stone.selected,
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.sidebar.base,
          borderRight: `1px solid ${theme.palette.divider}`,
          "& .MuiListItemIcon-root": {
            color: rawColor.stone.text.secondary,
          },
          "& .MuiListItemText-root": {
            color: rawColor.stone.text.primary,
          },
          "& .MuiListItemButton-root.Mui-selected": {
            backgroundColor: theme.sidebar.selected,
            "& .MuiListItemIcon-root": {
              color: rawColor.stone.main,
            },
            "& .MuiListItemText-root": {
              color: rawColor.stone.main,
            },
          },
          "& .MuiListItemButton-root:hover": {
            backgroundColor: theme.sidebar.hover,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          backgroundColor: colors.table.stone.cell,
          color: rawColor.stone.text.primary,
        },
        head: {
          backgroundColor: colors.table.stone.header,
          color: colors.table.stone.headerText,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: rawColor.stone.paper,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h5: {
          color: rawColor.stone.text.primary,
        },
        h6: {
          color: rawColor.stone.text.primary,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: rawColor.stone.text.primary,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: rawColor.stone.text.secondary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            color: "currentColor",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: rawColor.stone.text.secondary,
          "& .MuiInputBase-input": {
            color: rawColor.stone.text.secondary,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: rawColor.stone.text.secondary,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.stone.divider,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.stone.text.secondary,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.stone.main,
          },
          "& .MuiOutlinedInput-input": {
            color: rawColor.stone.text.secondary,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: rawColor.stone.text.secondary,
          "&.Mui-focused": {
            color: rawColor.stone.main,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: rawColor.stone.text.secondary,
        },
        select: {
          color: rawColor.stone.text.secondary,
        },
        selectLabel: {
          color: rawColor.stone.text.secondary,
        },
        selectIcon: {
          color: rawColor.stone.text.secondary,
        },
        displayedRows: {
          color: rawColor.stone.text.secondary,
        },
        actions: {
          color: rawColor.stone.text.secondary,
          "& .MuiSvgIcon-root": {
            color: rawColor.stone.text.secondary,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiAutocomplete-option": {
            color: rawColor.stone.text.secondary,
            "&:hover": {
              backgroundColor: rawColor.stone.sidebar.hover,
            },
            '&[aria-selected="true"]': {
              backgroundColor: rawColor.stone.sidebar.selected,
              color: rawColor.stone.main,
              "&:hover": {
                backgroundColor: rawColor.stone.sidebar.selected,
              },
            },
          },
          "& .MuiAutocomplete-listbox": {
            backgroundColor: rawColor.stone.paper,
            "& .MuiAutocomplete-option": {
              padding: "8px 16px",
            },
          },
          "& .MuiAutocomplete-option .MuiTypography-root": {
            color: "inherit",
          },
          "& .MuiAutocomplete-option .MuiListItemText-root": {
            color: "inherit",
          },
        },
        paper: {
          backgroundColor: rawColor.stone.paper,
          "& .MuiAutocomplete-option": {
            color: rawColor.stone.text.secondary,
          },
        },
        listbox: {
          "& .MuiAutocomplete-option": {
            color: rawColor.stone.text.secondary,
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        ".select__control": {
          backgroundColor: `${rawColor.stone.paper} !important`,
          borderColor: `${rawColor.stone.divider} !important`,
          "&:hover": {
            borderColor: `${rawColor.stone.text.secondary} !important`,
          },
          "&--is-focused": {
            borderColor: `${rawColor.stone.main} !important`,
            boxShadow: `0 0 0 1px ${rawColor.stone.main} !important`,
          },
        },
        ".select__menu": {
          backgroundColor: `${rawColor.stone.paper} !important`,
          border: `1px solid ${rawColor.stone.divider} !important`,
        },
        ".select__option": {
          backgroundColor: `${rawColor.stone.paper} !important`,
          color: `${rawColor.stone.text.secondary} !important`,
          "&:hover": {
            backgroundColor: `${rawColor.stone.sidebar.hover} !important`,
          },
          "&--is-focused": {
            backgroundColor: `${rawColor.stone.sidebar.hover} !important`,
          },
          "&--is-selected": {
            backgroundColor: `${rawColor.stone.sidebar.selected} !important`,
            color: `${rawColor.stone.main} !important`,
            "&:hover": {
              backgroundColor: `${rawColor.stone.sidebar.selected} !important`,
            },
          },
        },
        ".select__multi-value": {
          backgroundColor: `${rawColor.stone.sidebar.hover} !important`,
          "&__label": {
            color: `${rawColor.stone.text.secondary} !important`,
          },
          "&__remove": {
            color: `${rawColor.stone.text.secondary} !important`,
            "&:hover": {
              backgroundColor: `${rawColor.stone.sidebar.selected} !important`,
              color: `${rawColor.stone.main} !important`,
            },
          },
        },
        ".select__placeholder": {
          color: `${rawColor.stone.text.secondary} !important`,
        },
        ".select__single-value": {
          color: `${rawColor.stone.text.secondary} !important`,
        },
        ".select__input": {
          color: `${rawColor.stone.text.secondary} !important`,
        },
        ".select__indicator": {
          color: `${rawColor.stone.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.stone.main} !important`,
          },
        },
        ".select__indicator-separator": {
          backgroundColor: `${rawColor.stone.divider} !important`,
        },
        ".select__clear-indicator": {
          color: `${rawColor.stone.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.stone.main} !important`,
          },
        },
        ".select__dropdown-indicator": {
          color: `${rawColor.stone.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.stone.main} !important`,
          },
        },
      },
    },
  },
});
